import React, { ReactNode, useContext, useState, useEffect } from 'react';
import { cn } from '@helpers/className';
import { TabsContext } from './TabsContext';

type TabPanelProps = {
  children: ReactNode;
  value: string;
};

export default function TabPanel({ children, value }: TabPanelProps) {
  const context = useContext(TabsContext);
  const [isTransitioningIn, setIsTransitioningIn] = useState(false);
  const [isJavascriptEnabled, setIsJavascriptEnabled] = useState(false);

  if (!context) {
    throw new Error('TabPanel must be used within a Tabs component');
  }

  const { activeTab } = context;
  const isActive = activeTab === value;

  // Mark that JavaScript is available
  useEffect(() => {
    setIsJavascriptEnabled(true);
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (isActive && isJavascriptEnabled) {
      // Set a timeout to allow the component to mount with opacity-0
      // before transitioning to opacity-100.
      timer = setTimeout(() => {
        setIsTransitioningIn(true);
      }, 10); // A small delay is sufficient
    } else {
      setIsTransitioningIn(false); // Reset when the panel is no longer active
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isActive, isJavascriptEnabled]);

  const getClasses = () =>
    cn(
      // Default state without JavaScript - all content visible (progressive enhancement)
      !isJavascriptEnabled && 'block',

      // Base transition classes when JavaScript is available
      isJavascriptEnabled && 'transition-opacity duration-600 ease-out',

      // Active tab states
      isJavascriptEnabled && isActive && isTransitioningIn && 'opacity-100',
      isJavascriptEnabled && isActive && !isTransitioningIn && 'opacity-0',

      // Inactive tab states - hidden but present in DOM for SEO
      isJavascriptEnabled && !isActive && 'opacity-0 pointer-events-none h-0 overflow-hidden',
    );

  return (
    <div
      className={getClasses()}
      aria-hidden={isJavascriptEnabled ? !isActive : false}
      role="tabpanel"
      tabIndex={isActive ? 0 : -1}
      data-state={isActive ? 'active' : 'inactive'}
      data-tab-value={value}
    >
      {children}
    </div>
  );
}
